import * as React from 'react';
import * as _ from 'lodash';
import {
  Logging,
  hashObject,
  Image,
  UserInfo,
  UserContext,
  PermissionGroup,
  getViewPermission,
  CustomerSummary,
} from '@vcd-ecm-ui/Common';
import {
  VtxTabPane,
  VtxTabs,
} from '@vertexinc/vtx-ui-react-component-library';
import SideBySideViewerCertificateTab from './Tabs/sbsCertificateTab';
import SideBySideViewerContactTab from './Tabs/sbsContactTab';
import SideBySideViewerDocumentationTab from './Tabs/sbsDocumentationTab';
import SideBySideViewerChildCustomersTab from './Tabs/sbsChildCustomersTab';
import SideBySideViewerContentLeftProps from './sbsViewerContentLeftProps.interface';
import {
  additionalDocumentsExist,
} from '../../helpers/SideBySideViewer';
import getMediaSize from '../../helpers/media';
import '../sidebyside-viewer.scss';

const SideBySideViewerContentLeft = ({
  gatewayEndPoint,
  certificate,
  childCustomers,
  editable,
  customerContacts,
  selectedContact,
  setSelectedContact,
  onSaveContact,
  setEditedCertificate,
}: SideBySideViewerContentLeftProps): JSX.Element => {
  const [selectedTab, setSelectedTab] = React.useState('1');
  const [mediaSize, setMediaSize] = React.useState(getMediaSize());

  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);

  // Get the Contacts View Permission
  const permissionContactsView: boolean = getViewPermission(userContext, PermissionGroup.Contacts);

  // Get the Customers View Permission
  const permissionCustomersView: boolean = getViewPermission(userContext, PermissionGroup.Customers);

  const onSelectTab = (tab: string): void => {
    setSelectedTab(tab);
  };

  const getChildCustomers = (): JSX.Element[] => {
    Logging.Info('Child Customers', childCustomers);

    // eslint-disable-next-line arrow-body-style
    return childCustomers.map((customer: CustomerSummary) => {
      return (
        <div key={customer.customerUuid} className="field100 sbsChildCustomer">
          <div className="sbsChildCustomerCode">
            {
              permissionCustomersView
                ? (
                  <a className="sbsChildCustomerLink" href={`#/customer/view/${customer.customerUuid}/1`}>{customer.customerCode}</a>
                )
                : (
                  <>{customer.customerCode}</>
                )
            }
          </div>
          <div className="sbsChildCustomerName">
            {customer.customerName}
          </div>
        </div>
      );
    });
  };

  const getTabName = (tab: number, size: string): string => {
    if (tab === 1) {
      switch (size.toLowerCase()) {
        case 'xlarge':
        case 'medium':
          return 'Certificate Info';

        case 'large':
          return 'Cert. Info';

        case 'small':
          return 'Cert.';

        default:
          return null;
      }
    } else if (tab === 2) {
      switch (size.toLowerCase()) {
        case 'xlarge':
          return 'Additional Documentation';

        case 'large':
        case 'medium':
        case 'small':
          return 'Documentation';

        default:
          return null;
      }
    } else if (tab === 3) {
      switch (size.toLowerCase()) {
        case 'xlarge':
        case 'medium':
          return 'Contact Info';

        case 'large':
        case 'small':
          return 'Contact';

        default:
          return null;
      }
    } else if (tab === 4) {
      switch (size.toLowerCase()) {
        case 'xlarge':
        case 'medium':
          return 'Child Customers';

        case 'large':
        case 'small':
          return 'Children';

        default:
          return null;
      }
    }

    return null;
  };

  // Only the Certificate Info Tab (1) or Contact Tab (3) is enabled during Editing
  const isTabEnabled = (tab: number): boolean => (editable ? ((tab === 1) || (tab === 3)) : true);

  // Window was Resized
  const onResize = (): void => {
    // Adjust the Media Size based on the Window Width
    const size: string = getMediaSize();
    Logging.Debug('onResize', size);
    setMediaSize(size);
  };

  const additionalDocumentation: boolean = additionalDocumentsExist(certificate);

  // Edit Mode Changed
  React.useEffect(() => {
    // Edit Mode enabled - Switch to Tab 1 if not already 1 or 3
    if ((editable === true) && (selectedTab !== '1') && (selectedTab !== '3')) {
      // Select the Certificate Information Tab
      setSelectedTab('1');
    }
  }, [editable]);

  React.useEffect(() => {
    Logging.Debug('SBS Left Mount:');

    // Add to Window Resize Events
    window.addEventListener('resize', onResize);

    // This Function will run once the component unmounts (componentWillUnmount)
    return (): void => {
      Logging.Debug('SBS Left UnMount:');

      // Remove to Window Resize Events
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // No valid certificate
  if (!certificate) {
    return (<></>);
  }

  return (
    <>
      <div className="sbsContentLeft">
        <VtxTabs activeKey={selectedTab} onChange={onSelectTab}>
          <VtxTabPane tab={getTabName(1, mediaSize)} key="1" disabled={!isTabEnabled(1)}>
            <SideBySideViewerCertificateTab
              certificate={certificate}
              editable={editable}
              setEditedCertificate={setEditedCertificate}
            />
          </VtxTabPane>
          {
            additionalDocumentation
            && (
              <VtxTabPane tab={getTabName(2, mediaSize)} key="2" disabled={!isTabEnabled(2)}>
                <SideBySideViewerDocumentationTab
                  certificate={certificate}
                  editable={editable}
                />
              </VtxTabPane>
            )
          }
          {permissionContactsView && (
            <VtxTabPane tab={getTabName(3, mediaSize)} key="3" disabled={!isTabEnabled(3)}>
              <SideBySideViewerContactTab
                certificate={certificate}
                editable={editable}
                customerContacts={customerContacts}
                selectedContact={selectedContact}
                setSelectedContact={setSelectedContact}
                onSaveContact={onSaveContact}
              />
            </VtxTabPane>
          )}
          {childCustomers && (
            <VtxTabPane tab={getTabName(4, mediaSize)} key="4" disabled={!isTabEnabled(4)}>
              <SideBySideViewerChildCustomersTab
                certificate={certificate}
                editable={editable}
                childCustomers={childCustomers}
              />
            </VtxTabPane>
          )}
        </VtxTabs>
      </div>
    </>
  );
};

export default SideBySideViewerContentLeft;
